import * as React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import { useTheme } from '../../../theming/useTheme';
import { VrLineHeight } from '../../../theming/verticalRhythm/verticalRhythm';
import { MEDIA_BREAKPOINTS } from '../../../mediaQueries.const';
import { ErrorBoundary } from '../../errorHandling/ErrorBoundary';
import { Thumbnail } from '../../partials/image/image.types';
import { AdditionalStyleSheet } from '../../components.utils';
import { ImageWithFigcaption } from '../../partials/image/imageWithFigcaption/ImageWithFigcaption';
import { Image } from '../../partials/image/Image';
import { Jw } from '../media/Jw';
import { Iframe } from '../media/Iframe';
import { MMPlayer } from '../media/MMPlayer';
import { getImageCaption } from '../../partials/image/image.utils';

const componentConfigThemeKey = 'articleMainImage';

interface ArticleMainImageThemeProps {
  vrLineHeight: VrLineHeight
}

export interface JwCoverBlock {
  videoId: string
  html: string,
  type: string,
}

export interface HTMLCoverBlock {
  html: string,
  type: string,
}

export type Media = JwCoverBlock | HTMLCoverBlock;

interface ArticleMainImageDataProps {
  image: Thumbnail
  media: Media | null
  siteName: string
  templateName: string
}

type ArticleMainImageProps = ArticleMainImageDataProps & ArticleMainImageThemeProps;

const getMainImageStyle = (vrLineHeight: VrLineHeight) => {
  return StyleSheet.create({
    style: {
      [MEDIA_BREAKPOINTS.LARGE]: {
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
        marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.25}px`,
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
        marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.25}px`,
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
        marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.25}px`,
      },
    },
  });
};

const getCoverStyles = () => {
  return StyleSheet.create({
    wrapper: {
      position: 'relative',
    },
  });
};

const getBlockStyle = () => {
  return StyleSheet.create({
    style: {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%',
    },
  });
};

const getMainImage = (image: Thumbnail, credit: string | undefined, caption:string | undefined, style: AdditionalStyleSheet) => {
  const figCaption = getImageCaption(credit, caption);
  return figCaption
    ? <ImageWithFigcaption {...image} figcaption={figCaption} additionalStyle={style} />
    : <Image {...image} additionalStyle={style} />;
};

const getMedia = (media: Media, siteName: string, templateName: string) => {
  const additionalStyle = getBlockStyle() as AdditionalStyleSheet;
  const { type } = media;
  switch (type) {
    case 'jw': {
      const { html, videoId } = media as JwCoverBlock;
      return <Jw html={html} additionalStyle={additionalStyle} videoId={videoId} siteName={siteName} templateName={templateName} />;
    }

    case 'youtube': {
      const { html } = media as HTMLCoverBlock;
      return <Iframe html={html} additionalStyle={additionalStyle} />;
    }

    case 'mmPlayer': {
      const { html } = media as HTMLCoverBlock;
      return <MMPlayer html={html} additionalStyle={additionalStyle} />;
    }
    default: return null;
  }
};

const mapThemeProps = (theme: ArticleMainImageThemeProps) => ({
  vrLineHeight: theme.vrLineHeight,
});

export const ArticleMainImageComponent: React.FunctionComponent<ArticleMainImageProps> = ({ vrLineHeight, image, media, siteName, templateName }) => {
  const mainImageStyle = getMainImageStyle(vrLineHeight) as AdditionalStyleSheet;
  const coverStyles = getCoverStyles();
  const { credit, caption } = image;
  return (
    <div className={css(coverStyles.wrapper)}>
      {getMainImage(image, credit, caption, mainImageStyle)}
      {media && getMedia(media, siteName, templateName)}
    </div>
  );
};

export const ArticleMainImage = (props: ArticleMainImageDataProps) => {
  const themeContext = useTheme(mapThemeProps, componentConfigThemeKey);
  return <ErrorBoundary><ArticleMainImageComponent {...themeContext} {...props} /></ErrorBoundary>;
};
